import styled from '@emotion/styled'
import { Button } from 'components/button/Button'
import { Icon } from 'components/icon'
import { Spacer } from 'components/spacer/Spacer'
import { BlockSpinner } from 'components/spinner/Spinner'
import { Stack } from 'components/Stack'
import { OrangeTag } from 'components/Tag'
import {
  SLoadingContainer,
  SWizardCardDescription,
  SWizardCardDuration,
  SWizardCardHeader,
  SWizardCardItem,
  SWizardCardItemBig,
  SWizardCardTitle,
} from './WizardCardItem.styled'
import { AnimatePresence, motion } from 'framer-motion'
import { forwardRef, ReactNode } from 'react'
import { BREAKPOINTS } from 'utils/styled'

export const WizardCardItemBigAnim = forwardRef<
  HTMLDivElement,
  {
    ['data-testid']?: string
    className?: string
    onClick?: () => void
    onCloseClick?: () => void
    icon?: ReactNode
    duration?: ReactNode
    title: ReactNode
    description: ReactNode
    actionLabel?: ReactNode
    active?: boolean
    isLoading?: boolean
    disabled?: boolean
    disabledContent?: ReactNode
  }
>((props, ref) => {
  return (
    <SWizardCardItem
      active={props.active}
      disabled={props.disabled}
      onClick={props.onClick}
      className={props.className}
      isLoading={props.isLoading}
      data-testid={props['data-testid']}
      ref={ref}
    >
      {(props.isLoading || props.disabled) && (
        <SLoadingContainer>
          {props.isLoading && <BlockSpinner />}
          {props.disabledContent}
        </SLoadingContainer>
      )}
      <Stack space="0.5rem">
        <SWizardCardHeader>
          {props.icon}

          <AnimatePresence exitBeforeEnter>
            {props.onCloseClick && (
              <motion.span exit={{ opacity: 0 }}>
                <Button
                  renderAs="div"
                  type="button"
                  onClick={(e) => {
                    e?.preventDefault()
                    e?.stopPropagation()
                    props.onCloseClick?.()
                  }}
                  size="small"
                  variant="text"
                  tone="gray"
                >
                  <Icon name="Close" size={12} />
                </Button>
              </motion.span>
            )}
          </AnimatePresence>
        </SWizardCardHeader>

        <SWizardCardTitle>{props.title}</SWizardCardTitle>
        <SWizardCardDescription css={{ whiteSpace: 'pre-line', flex: '1' }}>
          {props.description}
        </SWizardCardDescription>

        <SWizardCardHeader css={{ alignItems: 'flex-end' }}>
          <Button variant="text" type="button" disabled={!props.onClick}>
            <AnimatePresence exitBeforeEnter>{props.actionLabel}</AnimatePresence>
          </Button>
          <AnimatePresence exitBeforeEnter>
            {props.duration && <SWizardCardDuration>{props.duration}</SWizardCardDuration>}
          </AnimatePresence>
        </SWizardCardHeader>
      </Stack>
    </SWizardCardItem>
  )
})
WizardCardItemBigAnim.displayName = 'WizardCardItemBigAnim'

export const WizardCardBigGrid5 = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(14.5rem, 1fr));
  grid-gap: 1rem;
`

export const WizardCardBigGrid4 = styled.div<{ autoFill?: boolean }>`
  display: grid;
  grid-template-columns: repeat(
    ${({ autoFill }) => (autoFill ? 'auto-fill' : 'auto-fit')},
    minmax(17rem, 1fr)
  );
  grid-gap: 1rem;

  @media ${BREAKPOINTS.MD.max} {
    grid-template-columns: 1fr;
  }
`

export const WizardCardBigGrid3 = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(20rem, 1fr));
  grid-gap: 1rem;
`

export const WizardCardBig = forwardRef<
  HTMLDivElement,
  {
    ['data-testid']?: string
    className?: string
    onClick?: () => void
    icon?: ReactNode
    duration?: ReactNode
    title: ReactNode
    description: ReactNode
    actionLabel?: ReactNode
    isLoading?: boolean
    disabled?: boolean
    tag?: string
    onInfoClick?: () => void
  }
>((props, ref) => {
  return (
    <SWizardCardItemBig
      ref={ref}
      data-testid={props['data-testid']}
      className={props.className}
      disabled={props.disabled}
      isLoading={props.isLoading}
      onClick={props.onClick}
    >
      <Stack space="0.25rem">
        <SWizardCardHeader>
          {props.icon}
          {props.tag && <OrangeTag>{props.tag}</OrangeTag>}
        </SWizardCardHeader>

        <SWizardCardTitle>{props.title}</SWizardCardTitle>
        <SWizardCardDescription css={{ whiteSpace: 'pre-line', flex: '1' }}>
          {props.description}
        </SWizardCardDescription>

        <Spacer size={0.75} />

        <SWizardCardHeader css={{ alignItems: 'flex-end' }}>
          <Button type="button" variant="round" disabled={props.disabled}>
            {props.actionLabel}
          </Button>

          {props.duration && <SWizardCardDuration>{props.duration}</SWizardCardDuration>}
        </SWizardCardHeader>
      </Stack>
    </SWizardCardItemBig>
  )
})
WizardCardBig.displayName = 'WizardCardBig'
